import { Grid, Skeleton } from '@mui/material';
import { CardBody, CardTitle } from '@apigrowfinance/pbolt-card';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import TextDisplay from '@apigrowfinance/pbolt-text-display';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage, useIntl } from 'react-intl';

import { CustomerDetails } from '@clients/customersClient';
import AccountHolderChip from '@components/chips/AccountHolderChip';
import InactiveChip from '@components/chips/InactiveChip';
import phoneGenderTypes from '@messages/phoneGenderTypes';
import phoneMaritalStatus from '@messages/phoneMaritalStatus';
import getCustomerName from '@utils/getCustomerName';
import getEnumTranslation from '@utils/getEnumTranslation';

type UserProps = {
    userDetails: CustomerDetails;
    isLoading?: boolean;
};

const UserRow = ({ userDetails, isLoading = false }: UserProps) => {
    const { theme } = useTheme();
    const { formatMessage } = useIntl();
    return (
        <>
            <CardTitle>
                {isLoading ? (
                    <Skeleton width={120} data-testid="user-title-loader" />
                ) : (
                    <Grid container sx={{ alignItems: 'center' }}>
                        <Grid item data-testid="printed-name">
                            {getCustomerName(userDetails?.customer)}
                        </Grid>
                        {userDetails?.customer?.is_owner && (
                            <Grid item sx={{ ml: 2 }}>
                                <AccountHolderChip />
                            </Grid>
                        )}
                        {userDetails?.customer?.is_active === false && (
                            <Grid item sx={{ ml: 2 }}>
                                <InactiveChip />
                            </Grid>
                        )}
                    </Grid>
                )}
            </CardTitle>

            <CardBody>
                <Grid container sx={{ rowGap: theme.spacing(2) }}>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="social-name"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.socialName"
                                    defaultMessage="Social name"
                                />
                            }
                            value={userDetails?.customer?.social_name}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="nickname"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.nickname"
                                    defaultMessage="Nickname"
                                />
                            }
                            value={userDetails?.customer?.nickname}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="is-active"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.isActive"
                                    defaultMessage="Active"
                                />
                            }
                            value={
                                userDetails?.customer?.is_active
                                    ? (getEnumTranslation('yes', {
                                          formatMessage: formatMessage,
                                      }) as string)
                                    : (getEnumTranslation('no', {
                                          formatMessage: formatMessage,
                                      }) as string)
                            }
                            isLoading={isLoading}
                        />
                    </Grid>
                    {/* Entity */}
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="document-number"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.documentNumber"
                                    defaultMessage="Document number"
                                />
                            }
                            value={userDetails?.entity?.document_number}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="registration"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.registration"
                                    defaultMessage="Registration"
                                />
                            }
                            value={userDetails?.entity?.registration}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="birth-date"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.birthDate"
                                    defaultMessage="Birth date"
                                />
                            }
                            value={
                                <DateFormatter
                                    date={userDetails?.entity?.birth_date}
                                />
                            }
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="gender"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.gender"
                                    defaultMessage="Gender"
                                />
                            }
                            value={
                                userDetails?.entity?.gender &&
                                getEnumTranslation(
                                    userDetails?.entity?.gender,
                                    {
                                        messages: phoneGenderTypes,
                                    },
                                )
                            }
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="mothers-name"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.mothersName"
                                    defaultMessage="Mothers name"
                                />
                            }
                            value={userDetails?.entity?.mothers_name}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextDisplay
                            testId="marital-status"
                            label={
                                <FormattedMessage
                                    id="AccountDetailsCustomer.maritalStatus"
                                    defaultMessage="Marital Status"
                                />
                            }
                            value={
                                userDetails?.entity?.marital_status &&
                                getEnumTranslation(
                                    userDetails?.entity?.marital_status,
                                    {
                                        messages: phoneMaritalStatus,
                                    },
                                )
                            }
                            isLoading={isLoading}
                        />
                    </Grid>
                </Grid>
            </CardBody>
        </>
    );
};

export default UserRow;
